import { Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { buttonVariants, NumberTicker } from "ui";
import { cn } from "utils/cn";
import useGetPageId from "../../hooks/use-get-pageid";
import AIBotScanCheckin from "./AiBotScanCheckin";
import ScrollingScanScreenshot from "./scrolling-scan-screenshot";

interface ScanningAnimationProps {
    imageUrl: string | null
    width?: number
    height?: number
    scanDuration?: number
    cornerSize?: number
}

export default function ScanAnimation({
    width = 264,
    height = 149,
    scanDuration = 4,
    cornerSize = 14,
    imageUrl,
}: ScanningAnimationProps) {
    const { pageId } = useGetPageId();
    const fullPageThumbnail = `https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/uploads/design-review/pages/${pageId}/full-page-thumbnail.webp`

    const { t } = useTranslation('collaborate', { useSuspense: false });
    const [fullResLoaded, setFullResLoaded] = useState(false);
    const [fullResError, setFullResError] = useState(false);
    const [imageUrlLoaded, setImageUrlLoaded] = useState(false);
    const [imageLoadAttempts, setImageLoadAttempts] = useState(0);

    // Determine which image to use for the scan animation
    // Use fullPageThumbnail if available (more accurate), otherwise use imageUrl
    const scanImageUrl = fullResLoaded ? fullPageThumbnail : (imageUrlLoaded ? imageUrl : null);
    const showScanAnimation = fullResLoaded || imageUrlLoaded;

    // Retry loading the full page thumbnail periodically
    useEffect(() => {
        if (!fullResLoaded && !fullResError) {
            const interval = setInterval(() => {
                setImageLoadAttempts(prev => prev + 1);
            }, 2000); // Retry every 2 seconds

            return () => clearInterval(interval);
        }
    }, [fullResLoaded, fullResError]);

    const handleFullResLoad = () => {
        setFullResLoaded(true);
        setFullResError(false);
    };

    const handleFullResError = () => {
        setFullResError(true);
    };

    const handleImageUrlLoad = () => {
        setImageUrlLoaded(true);
    };

    const handleImageUrlError = () => {
        // Image URL failed to load, but we don't need to track this error
        // as we'll just show the loader if no images are available
    };

    const renderScanAnimation = () => (
        <div className="relative overflow-hidden bg-background rounded-lg shadow-md" style={{ width, height }}>
            <ScrollingScanScreenshot imageUrl={scanImageUrl!} />

            {/* Grayscale Image Layer */}
            <div
                className="absolute inset-0 overflow-hidden "
                style={{
                    animation: `revealGrayscale ${scanDuration}s linear infinite`,
                    width,
                    height,
                }}
            >
                <ScrollingScanScreenshot imageUrl={scanImageUrl!} className="grayscale" />
            </div>

            {/* Corner Borders */}
            <motion.div
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.7 }}
                transition={{
                    duration: 0.3, delay: 0.3,
                }}
                key={"scan-corners"}
                className="absolute inset-0 pointer-events-none">
                <div
                    className="absolute top-2 right-2 border-r-4 border-t-4 border-tertiary"
                    style={{ width: cornerSize, height: cornerSize }}
                />
                <div
                    className="absolute bottom-2 left-2 border-l-4 border-b-4 border-tertiary"
                    style={{ width: cornerSize, height: cornerSize }}
                />
                <div
                    className="absolute bottom-2 right-2 border-r-4 border-b-4 border-tertiary"
                    style={{ width: cornerSize, height: cornerSize }}
                />
            </motion.div>

            {/* Scanning Line */}
            <>
                <div
                    className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-tertiary to-transparent shadow-lg"
                    style={{
                        animation: `scan ${scanDuration}s linear infinite`,
                        boxShadow: "0 0 20px rgba(109, 93, 243, 0.8)",
                    }}
                />
            </>
            <div className="absolute top-2 left-2 flex items-center space-x-1 bg-tertiary px-2 py-1 rounded-md">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white text-xs font-medium ">{t('sidebar.scanning', 'Scanning ')}<span>
                    <NumberTicker value={100} duration={24} className="text-white" />%</span>
                </span>
            </div>
        </div>
    );

    const renderLoader = () => (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative overflow-hidden bg-gray-200 rounded-lg shadow-md"
            style={{ width, height }}
        >
            <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-rotate-360 repeat-infinite text-gray-400" />
            </div>
        </motion.div>
    );

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1 }}
            key={"scan-animation"}
            className="flex flex-col items-center justify-center absolute left-0 top-0 h-full w-full bg-background/90 backdrop-blur-sm z-[20] gap-4">

            {/* Hidden image to preload fullPageThumbnail */}
            <img
                src={fullPageThumbnail}
                alt=""
                style={{ display: "none" }}
                onLoad={handleFullResLoad}
                onError={handleFullResError}
                key={`full-thumbnail-${imageLoadAttempts}`} // Force re-render on retry
            />

            {/* Hidden image to preload imageUrl if provided */}
            {imageUrl && (
                <img
                    src={imageUrl}
                    alt=""
                    style={{ display: "none" }}
                    onLoad={handleImageUrlLoad}
                    onError={handleImageUrlError}
                />
            )}

            {/* Show scan animation when either image is loaded */}
            {showScanAnimation && renderScanAnimation()}

            {/* Show loader when no images are loaded */}
            <AnimatePresence>
                {!showScanAnimation && renderLoader()}
            </AnimatePresence>

            {/* AI Bot Checkin Component */}
            <AIBotScanCheckin />
            <div className="flex flex-col items-center text-xs absolute bottom-4">
                <span className="text-textcolor">{t('sidebar.info.screenshot', 'Screenshots not very accurate?')}</span>
                <span className="flex flex-row gap-1 text-textcolor">{t('sidebar.info.fix', 'Fix by installing our ')}
                    <a href='https://chromewebstore.google.com/detail/atarim-visual-website-col/ekpmbeilnaahlccpgjchjjoonecikehf'
                        target="_blank"
                        className={cn(
                            buttonVariants({
                                variant: 'link',
                                size: 'link',
                            }),
                            'text-xs font-medium'
                        )}>{t('chrome_extension', 'Chrome Extension')}</a></span>
            </div>
        </motion.div>
    );
}